import { memo, useEffect, useRef, useState } from 'react'
import quoteIcon from '@/assets/boxicons_quote-left-filled.svg'
import { motion } from 'motion/react'
import { cn } from '@/lib/cn'
import { usePrefersReducedMotion } from '@/lib/motion'

/** Card shell from assets/Rectangle 9578.svg — solid white in light mode, black in dark. */
const TESTIMONIAL_CARD_PATH =
  'M0 20C0 8.95431 8.95431 0 20 0H280C291.046 0 300 8.95431 300 20V253C300 264.046 291.046 273 280 273H197.5C186.454 273 177.5 264.046 177.5 253V241.5C177.5 230.454 168.546 221.5 157.5 221.5H20C8.95431 221.5 0 212.546 0 201.5V20Z'

type Testimonial = {
  id: string
  quote: string
  name: string
  roleLine: string
  initials: string
}

const CARD_HEIGHT = 273
const CARD_GAP = 36
const CONTAINER_MAX = 1200
/** Profile block width vs card — matches SVG cutout (~197.5 / 300). */
const PROFILE_BLOCK_WIDTH = '66%'
const AUTO_MS = 5200
const SLIDE_MS = 900

const SLIDES: Testimonial[] = [
  {
    id: 'founder',
    quote:
      "I had been sitting on this idea for two years. VAAGON gave me the external signal I needed to finally move. The community didn't just validate it, they sharpened it.",
    name: 'Ananya Rao',
    roleLine: 'Founder, FlowLedger',
    initials: 'AR',
  },
  {
    id: 'investor',
    quote:
      'As an investor, I use VAAGON to see what problems founders are actively trying to solve. The Leaderboard tells me more about market interest than most pitch decks.',
    name: 'Rohit Menon',
    roleLine: 'Early-stage investor',
    initials: 'RM',
  },
  {
    id: 'd2c',
    quote:
      'The Expert Validation report saved me from a six-month detour. Worth every rupee.',
    name: 'Meera Shah',
    roleLine: 'D2C founder',
    initials: 'MS',
  },
  {
    id: 'operator',
    quote:
      'Validating ideas here helped me spot weak assumptions before they turned into expensive mistakes for founders.',
    name: 'Karan Iyer',
    roleLine: 'Product operator',
    initials: 'KI',
  },
  {
    id: 'saas',
    quote:
      'The validation feedback was sharper than any mentor session I had paid for before.',
    name: 'Priya Nair',
    roleLine: 'SaaS founder',
    initials: 'PN',
  },
  {
    id: 'mentor',
    quote:
      'I recommend VAAGON to every founder in my cohort who is still guessing whether to build.',
    name: 'Arjun Desai',
    roleLine: 'Startup mentor',
    initials: 'AD',
  },
]

function formatDisplayName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return fullName
  const first = parts[0]
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase()
  return `${first} ${lastInitial}.`
}

function useCarouselLayout() {
  const [layout, setLayout] = useState({
    cardWidth: 360,
    gap: CARD_GAP,
    visibleCount: 3,
  })

  useEffect(() => {
    const update = () => {
      const gap = CARD_GAP
      const horizontalPadding =
        window.innerWidth >= 1024 ? 64 : window.innerWidth >= 640 ? 48 : 32
      const container = Math.min(
        window.innerWidth - horizontalPadding,
        CONTAINER_MAX,
      )

      if (window.innerWidth < 640) {
        setLayout({
          cardWidth: container,
          gap: 0,
          visibleCount: 1,
        })
        return
      }

      if (window.innerWidth < 1024) {
        setLayout({
          cardWidth: Math.floor((container - gap) / 2),
          gap,
          visibleCount: 2,
        })
        return
      }

      setLayout({
        cardWidth: Math.floor((container - 2 * gap) / 3),
        gap,
        visibleCount: 3,
      })
    }

    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return layout
}

function QuoteMarkIcon() {
  return (
    <img
      src={quoteIcon}
      alt=""
      aria-hidden
      className="h-7 w-7 shrink-0 object-contain brightness-0 dark:brightness-100"
      draggable={false}
    />
  )
}

function TestimonialCardShape({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 h-full w-full',
        className,
      )}
      viewBox="0 0 300 273"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d={TESTIMONIAL_CARD_PATH}
        className="fill-[#ffffff] dark:fill-white/5"
      />
    </svg>
  )
}

function TestimonialCard({ t, width }: { t: Testimonial; width: number }) {
  return (
    <article
      style={{ width, height: CARD_HEIGHT }}
      className="relative shrink-0 overflow-hidden rounded-[20px]"
      aria-label={`${t.quote} — ${t.name}`}
    >
      <TestimonialCardShape />

      <div className="relative z-10 flex h-full flex-col">
        <div className="px-6 pt-4 pb-14 sm:pt-6">
          <QuoteMarkIcon />
          <p className="mt-3 line-clamp-4 text-left text-[13px] leading-[1.55] font-medium text-black sm:text-[14px] dark:text-white">
            {t.quote}
          </p>
        </div>

        {/* Bottom-left SVG cutout — profile + name sit in the gap */}
        <div
          className="absolute -bottom-2 -left-2 z-10 flex min-w-0 items-center gap-3 px-3 py-2.5"
          style={{ width: PROFILE_BLOCK_WIDTH, maxWidth: 207 }}
        >
          <span
            className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-[#2A2A2A] text-[11px] font-semibold text-[#ffffff] ring-1 ring-black/10 dark:ring-white/10"
            aria-hidden
          >
            {t.initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] leading-tight font-bold text-white">
              {formatDisplayName(t.name)}
            </p>
            <p className="mt-0.5 text-[13px] leading-snug text-white/70">
              {t.roleLine}
            </p>
          </div>
        </div>
      </div>
    </article>
  )
}

export const TestimonialsCarousel = memo(() => {
  const reduceMotion = usePrefersReducedMotion()
  const { cardWidth, gap, visibleCount } = useCarouselLayout()
  const viewportWidth =
    visibleCount * cardWidth + Math.max(0, visibleCount - 1) * gap
  const loopSlides = [...SLIDES, ...SLIDES]
  const [activeIndex, setActiveIndex] = useState(0)
  const [animateSlide, setAnimateSlide] = useState(true)
  const resetTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (reduceMotion) return

    const timer = window.setInterval(() => {
      setAnimateSlide(true)
      setActiveIndex((current) => current + 1)
    }, AUTO_MS)

    return () => window.clearInterval(timer)
  }, [reduceMotion])

  useEffect(() => {
    if (reduceMotion || activeIndex < SLIDES.length) return

    resetTimerRef.current = window.setTimeout(() => {
      setAnimateSlide(false)
      setActiveIndex(0)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateSlide(true))
      })
    }, SLIDE_MS)

    return () => {
      if (resetTimerRef.current != null) {
        window.clearTimeout(resetTimerRef.current)
      }
    }
  }, [activeIndex, reduceMotion])

  const trackOffset = activeIndex * (cardWidth + gap)

  return (
    <section
      className="w-full bg-[var(--background)] px-4 pt-6 pb-10 sm:px-6 sm:pt-8 sm:pb-12 lg:pt-10 lg:pb-14"
      aria-labelledby="testimonials-heading"
    >
      <div className="mx-auto w-full max-w-[1200px]">
        <header className="mb-8 text-left sm:mb-10">
          <h2
            id="testimonials-heading"
            className="text-[28px] leading-tight font-medium tracking-tight text-[var(--foreground)]"
          >
            What other founders are saying
          </h2>
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed font-medium text-[var(--foreground-muted)] sm:text-[15px] dark:text-white/60">
            See why founders rely on our community for early validation
          </p>
        </header>

        <div
          className="w-full overflow-hidden max-sm:rounded-[20px]"
          style={{ maxWidth: viewportWidth }}
        >
          <motion.div
            className="flex w-full will-change-transform max-sm:max-w-full"
            style={{ gap }}
            animate={{ x: -trackOffset }}
            transition={
              reduceMotion || !animateSlide
                ? { duration: 0 }
                : {
                    type: 'tween',
                    duration: SLIDE_MS / 1000,
                    ease: [0.25, 0.8, 0.4, 1],
                  }
            }
            aria-live="polite"
          >
            {loopSlides.map((t, i) => (
              <TestimonialCard key={`${t.id}-${i}`} t={t} width={cardWidth} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  )
})

TestimonialsCarousel.displayName = 'TestimonialsCarousel'
